import { useState, useEffect, useCallback } from 'react';
import PWAInstallService from '../services/pwaInstallService';

interface UsePWAInstallReturn {
  isInstallable: boolean;
  isStandalone: boolean;
  platformInfo: any;
  installMethod: 'native' | 'ios-safari' | 'manual' | 'none';
  shouldShowBanner: boolean;
  install: () => Promise<boolean>;
  dismissBanner: (permanent?: boolean) => void;
  getInstallInstructions: () => { title: string; steps: string[]; icon: string };
}

export const usePWAInstall = (): UsePWAInstallReturn => {
  const [installService] = useState(() => PWAInstallService.getInstance());
  const [isInstallable, setIsInstallable] = useState(false);
  const [platformInfo, setPlatformInfo] = useState(installService.getPlatformInfo());
  const [installMethod, setInstallMethod] = useState(installService.getInstallMethod());
  const [shouldShowBanner, setShouldShowBanner] = useState(false);

  useEffect(() => {
    const updateState = () => {
      const platform = installService.getPlatformInfo();
      const method = installService.getInstallMethod();
      const installable = installService.isAppInstallable();
      const showBanner = installService.shouldShowInstallBanner();

      setPlatformInfo(platform);
      setInstallMethod(method);
      setIsInstallable(installable);
      setShouldShowBanner(showBanner);
    };

    updateState();

    // Listen for beforeinstallprompt events to update state
    const handleBeforeInstallPrompt = () => {
      console.log('🔗 Hook: beforeinstallprompt event detected');
      setTimeout(updateState, 100); // Small delay to ensure service is updated
    };

    // Listen for custom events from the service
    const handlePWAInstallAvailable = () => {
      console.log('🔗 Hook: PWA install available event');
      setTimeout(updateState, 100);
    };

    const handlePWAInstalled = () => {
      console.log('🔗 Hook: PWA installed event');
      setTimeout(updateState, 100);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', updateState);
    window.addEventListener('pwa-install-available', handlePWAInstallAvailable);
    window.addEventListener('pwa-installed', handlePWAInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', updateState);
      window.removeEventListener('pwa-install-available', handlePWAInstallAvailable);
      window.removeEventListener('pwa-installed', handlePWAInstalled);
    };
  }, [installService]);

  const install = useCallback(async (): Promise<boolean> => {
    const result = await installService.triggerInstallPrompt();
    
    // Update state after install attempt
    setTimeout(() => {
      setIsInstallable(installService.isAppInstallable());
      setInstallMethod(installService.getInstallMethod());
      setShouldShowBanner(installService.shouldShowInstallBanner());
    }, 100);
    
    return result;
  }, [installService]);

  const dismissBanner = useCallback((permanent: boolean = false) => {
    installService.recordBannerDismissed(permanent);
    setShouldShowBanner(false);
  }, [installService]);

  const getInstallInstructions = useCallback(() => {
    return installService.getInstallInstructions();
  }, [installService]);

  return {
    isInstallable,
    isStandalone: platformInfo.isStandalone,
    platformInfo,
    installMethod,
    shouldShowBanner,
    install,
    dismissBanner,
    getInstallInstructions,
  };
}; 